import { BEIGE_300, BEIGE_600, GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { FC, Fragment, useEffect, useMemo, useRef } from 'react'
import { MeasurementInfoCard, measurementInfoCardIconSx } from 'components/common/MeasurementInfoCard'
import { MeasurementReviewInfo } from 'components/common/MeasurementReviewInfo'
import { ProductKind, ProductType } from 'constants/product'
import { Trans, useTranslation } from 'react-i18next'

import { BorderBoxWrapper } from 'components/common/BorderBoxWrapper'
import Stack from '@mui/material/Stack'
import { SystemMessage } from 'components/common/SystemMessage'
import Typography from '@mui/material/Typography'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import { logger } from 'utils/logging'
import { useProductAndInstructionLists } from 'components/pages/PurchaseFlow/_main/contexts/ProductAndInstructionLists.context'
import { usePurchaseFlowProducts } from 'components/pages/PurchaseFlow/_main/contexts/PurchaseFlowProducts.context'

/** Stands for the Cost Calculation Notice card, the redesigned measurement flavour of the floor-plan disclaimer. */
const COST_CALCULATION_NOTICE = 'cost_calculation_notice'

/**
 * @component
 * Read-only recap of the ordered products for the embed order summary (ISSUE-3099). Flat text lines in
 * a single box: the order was priced and configured in the previous steps, so this last step only
 * restates what was ordered, without prices, quantity controls or any selection. The floor-plan
 * disclaimers ProductListing shows per card follow below the box, deduplicated.
 *
 * @example <EmbedProductRecap />
 */
export const EmbedProductRecap: FC = () => {
  const { t } = useTranslation(['order'])
  const { getProductFullList } = useProductAndInstructionLists()
  const { selectedProducts, selectedProductOptions } = usePurchaseFlowProducts()

  const products = getProductFullList()
  const emptyWarningRef = useRef(false)

  // An embed reaching the last step with nothing to recap means the selection was lost upstream.
  useEffect(() => {
    if (products.length || emptyWarningRef.current) return
    emptyWarningRef.current = true
    logger.warn('Embed order summary rendered an empty product recap', undefined, {
      context: { component: 'EmbedProductRecap', action: 'emptyProductList' },
    })
  }, [products.length])

  // The flat recap has no per-product card to hang ProductListing's disclaimers on, so each distinct
  // one is rendered at most once below the box.
  const disclaimers = useMemo(() => {
    const distinct = new Set<string>()

    for (const product of Object.values(selectedProducts)) {
      const has3D = Object.values(selectedProductOptions[product.id] ?? {}).some((option) => option.kind === ProductKind.FLOOR_PLAN_EDITING_3D)
      if (product.type !== ProductType.FLOOR_PLAN || has3D) continue

      if (product.kind === ProductKind.MEASUREMENT_ON_SITE) distinct.add(COST_CALCULATION_NOTICE)
      else distinct.add('step_validation.floor_plan_disclaimer_shooting')
    }

    return [...distinct]
  }, [selectedProductOptions, selectedProducts])

  // The recap sits in a plain block box, so the gap here is what separates it from the disclaimers.
  return (
    <Stack gap={2}>

      <BorderBoxWrapper
        padding={1.5}
        elevation='none'
        borderColor={BEIGE_600}
        backgroundColor={BEIGE_300}
        data-testid="embed-product-recap"
      >
        {products.length
          ? <Stack component="ul" gap={0.5} sx={{ listStyleType: 'none', margin: 0, padding: 0 }}>
            {products.map((product) => (
              <Typography
                component="li"
                key={`${product.id}-${product.isOption}`}
                fontWeight={600}
                variant={product.isOption ? 'text-sm' : 'text-md'}
                color={product.isOption ? GRAY_700 : GRAY_900}
                sx={product.isOption ? { listStyleType: 'disc', display: 'list-item', marginLeft: 3 } : undefined}
              >
                {product.count > 1 ? `${product.count} x ${product.name}` : product.name}
              </Typography>
            ))}
          </Stack>
          : <Typography variant="text-md" color={GRAY_700}>
            {t('cart.no_product_selected')}
          </Typography>
        }
      </BorderBoxWrapper>

      {disclaimers.map((disclaimer) => disclaimer === COST_CALCULATION_NOTICE
        ? <Fragment key={disclaimer}>
          <MeasurementInfoCard
            icon={<WarningAmberOutlinedIcon sx={measurementInfoCardIconSx} />}
            title={t('step_validation.cost_calculation_notice_title')}
          >
            <Typography variant="text-sm" color={GRAY_900}>
              {t('step_validation.cost_calculation_notice_text')}
            </Typography>
          </MeasurementInfoCard>

          <MeasurementReviewInfo />
        </Fragment>
        : <SystemMessage
          key={disclaimer}
          variant="warning"
          message={<Trans
            t={t}
            i18nKey={disclaimer}
            components={{
              list: <ul />,
              item: <li />,
              bold: <strong />,
            }}
          >
            &nbsp;
          </Trans>}
        />
      )}

    </Stack>
  )
}
